/**
 * ImageCropService.ts
 * Service pour gérer le recadrage d'images
 */
import Konva from 'konva';
import { logger } from '../utils/ProfessionalLogger';

export interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Recadre une image selon une zone spécifiée
 * @param image - L'élément image HTML
 * @param cropArea - La zone de recadrage
 * @returns Promise avec le Blob de l'image recadrée
 */
export async function cropImage(image: HTMLImageElement, cropArea: CropArea): Promise<Blob> {
  return new Promise((resolve, reject) => {
    try {
      // Créer un canvas pour le recadrage
      const canvas = document.createElement('canvas');
      canvas.width = cropArea.width;
      canvas.height = cropArea.height;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error("Impossible d'obtenir le contexte du canvas"));
        return;
      }

      // Dessiner l'image recadrée sur le canvas
      ctx.drawImage(
        image,
        cropArea.x,
        cropArea.y,
        cropArea.width,
        cropArea.height,
        0,
        0,
        cropArea.width,
        cropArea.height
      );

      // Convertir en Blob
      canvas.toBlob(
        blob => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Impossible de créer le Blob'));
          }
        },
        'image/png',
        1.0
      );
    } catch (error) {
      logger.error("Erreur lors du recadrage de l'image:", error);
      reject(error);
    }
  });
}

/**
 * Applique le recadrage à un nœud Konva Image
 * @param node - Le nœud Konva Image
 * @param cropData - Les données de recadrage
 */
export function applyCropToKonvaNode(node: Konva.Image, cropData: CropArea): void {
  try {
    const image = node.image();
    if (!image) {
      logger.warn("Le nœud Konva n'a pas d'image");
      return;
    }

    // Créer un canvas temporaire pour le recadrage
    const canvas = document.createElement('canvas');
    canvas.width = cropData.width;
    canvas.height = cropData.height;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      logger.error("Impossible d'obtenir le contexte du canvas");
      return;
    }

    // Dessiner l'image recadrée
    ctx.drawImage(
      image,
      cropData.x,
      cropData.y,
      cropData.width,
      cropData.height,
      0,
      0,
      cropData.width,
      cropData.height
    );

    // Créer une nouvelle image depuis le canvas
    const croppedImage = new window.Image();
    croppedImage.crossOrigin = 'anonymous';
    croppedImage.onload = () => {
      node.image(croppedImage);
      node.width(cropData.width);
      node.height(cropData.height);
      node.getLayer()?.batchDraw();
      logger.info('✅ Image recadrée appliquée au nœud Konva');
    };
    croppedImage.onerror = error => {
      logger.error("Erreur lors du chargement de l'image recadrée:", error);
    };
    croppedImage.src = canvas.toDataURL('image/png');
  } catch (error) {
    logger.error("Erreur lors de l'application du recadrage:", error);
  }
}

/**
 * Convertit un Blob en URL de données
 * @param blob - Le Blob à convertir
 * @returns Promise avec l'URL de données
 */
export async function blobToDataURL(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
